import React, { useCallback } from "react";
import ExcelJS from "exceljs";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import {
  Document,
  Font,
  Page,
  StyleSheet,
  Text,
  View,
  pdf,
} from "@react-pdf/renderer";
import { showToast } from "services/utils/Status";
import RobotoRegular from "../../assets/fonts/Roboto/Roboto-Regular.ttf";
import RobotoBold from "../../assets/fonts/Roboto/Roboto-Bold.ttf";

// Built-in Helvetica has no rupee sign (₹), so PDFs use the bundled Roboto font.
const PDF_FONT_FAMILY = "ExportRoboto";
Font.register({
  family: PDF_FONT_FAMILY,
  fonts: [
    { src: RobotoRegular, fontWeight: 400 },
    { src: RobotoBold, fontWeight: 700 },
  ],
});

const pdfStyles = StyleSheet.create({
  page: {
    padding: 28,
    fontSize: 9,
    color: "#1f2937",
    fontFamily: PDF_FONT_FAMILY,
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 12,
    color: "#2e5e4e",
    textAlign: "center",
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    marginTop: 10,
    marginBottom: 6,
    color: "#2e5e4e",
  },
  metadataGrid: {
    borderWidth: 1,
    borderColor: "#c7ddd4",
    borderRadius: 4,
    overflow: "hidden",
    marginBottom: 10,
  },
  metadataRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#d8e9e1",
  },
  metadataLabel: {
    width: "23%",
    backgroundColor: "#ddefe8",
    padding: 6,
    fontWeight: 700,
  },
  metadataValue: {
    width: "27%",
    padding: 6,
  },
  table: {
    borderWidth: 1,
    borderColor: "#c7ddd4",
    borderRadius: 4,
    overflow: "hidden",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#d8e9e1",
  },
  headerCell: {
    backgroundColor: "#3c8d72",
    color: "#ffffff",
    fontWeight: 700,
    padding: 6,
    flex: 1,
  },
  bodyCell: {
    padding: 6,
    flex: 1,
  },
  serialCell: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 36,
    width: 36,
    paddingHorizontal: 4,
    textAlign: "center",
  },
  evenRow: {
    backgroundColor: "#f4fbf8",
  },
});

export const MAX_PDF_ROWS = 500;
export const MAX_PDF_COLUMNS = 10;

const getPdfTableScale = (columnCount) => {
  if (columnCount <= 8) return { fontSize: 9, padding: 6 };
  if (columnCount <= 12) return { fontSize: 8, padding: 5 };
  if (columnCount <= 16) return { fontSize: 7, padding: 4 };
  if (columnCount <= 22) return { fontSize: 6.5, padding: 3 };
  return { fontSize: 6, padding: 2 };
};

const PDF_PORTRAIT_MAX_COLUMNS = 6;

const getPdfOrientation = (columnCount) =>
  columnCount <= PDF_PORTRAIT_MAX_COLUMNS ? "portrait" : "landscape";

const excelColors = {
  navy: "FF2E5E4E",
  blue: "FF3C8D72",
  lightBlue: "FFE6F3ED",
  paleBlue: "FFF8FCFA",
  rowAlt: "FFF1F7F4",
  white: "FFFFFFFF",
  border: "FFC7DDD4",
  text: "FF1F2937",
  mutedText: "FF475569",
  highlight: "FFF2FAF5",
  success: "FFEDF8F1",
  warning: "FFFFF8E8",
  danger: "FFFDEEEE",
  info: "FFEEF5FC",
  neutral: "FFF7F8FA",
  grandTotal: "FFF4F8FF",
};

export const getFilteredHeaders = (headers = []) =>
  headers.filter((header) => {
    const headerName = (header.name || "").toLowerCase().trim();
    return (
      headerName !== "" && headerName !== "action" && headerName !== "actions"
    );
  });

const SERIAL_NUMBER_KEY = "__serial_number__";
const SUMMARY_ROW_TYPES = new Set([
  "department_header",
  "department_total",
  "grand_total",
]);

const getNestedValue = (obj, path) => {
  if (!path) return "";

  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : "";
  }, obj);
};

const formatBooleanValue = (value) => {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "string") {
    if (value.toLowerCase() === "true") return "Yes";
    if (value.toLowerCase() === "false") return "No";
  }

  return value;
};

const formatNumberFilterValue = (value, operator) => {
  const operatorSymbols = {
    equals: "=",
    notEquals: "!=",
    greaterEquals: ">=",
    lessEquals: "<=",
    between: "Between",
  };

  const symbol = operatorSymbols[operator] || "=";

  if (operator === "between" && value.fromNumber && value.toNumber) {
    return `${symbol} ${value.fromNumber} - ${value.toNumber}`;
  }
  if (operator === "between" && value.fromNumber) {
    return `${symbol} From ${value.fromNumber}`;
  }
  if (operator === "between" && value.toNumber) {
    return `${symbol} To ${value.toNumber}`;
  }
  if (value.number !== undefined && value.number !== "") {
    return `${symbol} ${value.number}`;
  }

  return "";
};

const roundToTwoDecimals = (value) => {
  const rounded = Number(value.toFixed(2));
  return Number.isNaN(rounded) ? value : rounded;
};

const getCellValue = (item, header) => {
  let value;

  if (header.customFormatter) {
    value = header.customFormatter(item);
  } else {
    value = getNestedValue(item, header.key);
    if (value === null || value === undefined || value === "") {
      // Summary/total rows only ever set the columns they actually
      // total (e.g. a TOTAL row's Employee Id/Department cells) — those
      // should render blank, not "N/A", which is reserved for genuinely
      // missing data on real rows.
      value = SUMMARY_ROW_TYPES.has(item?.rowType) ? "" : "N/A";
    }
  }

  if (typeof value === "number" && !Number.isInteger(value)) {
    value = roundToTwoDecimals(value);
  }

  return formatBooleanValue(value);
};

const buildFilterSummary = (appliedFilters = {}, headers = []) => {
  const formattedFilters = [];

  Object.entries(appliedFilters).forEach(([field, value]) => {
    if (!value) return;

    const matchedHeader = headers.find((header) => header.key === field);
    const displayName = matchedHeader ? matchedHeader.name : field;

    if (typeof value === "object") {
      if (value.date !== undefined) {
        if (value.operator === "equals" && value.date) {
          formattedFilters.push([displayName, value.date]);
        } else if (value.operator === "greater" && value.date) {
          formattedFilters.push([displayName, `> ${value.date}`]);
        } else if (value.operator === "less" && value.date) {
          formattedFilters.push([displayName, `< ${value.date}`]);
        } else if (
          value.operator === "between" &&
          value.fromDate &&
          value.toDate
        ) {
          formattedFilters.push([
            displayName,
            `${value.fromDate} to ${value.toDate}`,
          ]);
        } else if (value.operator === "between" && value.fromDate) {
          formattedFilters.push([displayName, `From ${value.fromDate}`]);
        } else if (value.operator === "between" && value.toDate) {
          formattedFilters.push([displayName, `To ${value.toDate}`]);
        }
      } else if (value.time !== undefined) {
        if (value.operator === "equals" && value.time) {
          formattedFilters.push([displayName, value.time]);
        } else if (value.operator === "greater" && value.time) {
          formattedFilters.push([displayName, `> ${value.time}`]);
        } else if (value.operator === "less" && value.time) {
          formattedFilters.push([displayName, `< ${value.time}`]);
        } else if (
          value.operator === "between" &&
          value.fromTime &&
          value.toTime
        ) {
          formattedFilters.push([
            displayName,
            `${value.fromTime} to ${value.toTime}`,
          ]);
        } else if (value.operator === "between" && value.fromTime) {
          formattedFilters.push([displayName, `From ${value.fromTime}`]);
        } else if (value.operator === "between" && value.toTime) {
          formattedFilters.push([displayName, `To ${value.toTime}`]);
        }
      } else if (
        value.number !== undefined ||
        value.fromNumber !== undefined ||
        value.toNumber !== undefined
      ) {
        const formattedValue = formatNumberFilterValue(value, value.operator);
        if (formattedValue) {
          formattedFilters.push([displayName, formattedValue]);
        }
      } else if (Array.isArray(value)) {
        if (value.length > 0 && !(value.length === 1 && value[0] === "All")) {
          formattedFilters.push([displayName, value.join(", ")]);
        }
      } else if (value.value !== undefined) {
        formattedFilters.push([
          displayName,
          value.value === "true" ? "Yes" : "No",
        ]);
      }
    } else {
      if (value === "All" || value === "") return;

      if (value === "true" || value === "false") {
        formattedFilters.push([displayName, value === "true" ? "Yes" : "No"]);
      } else {
        formattedFilters.push([displayName, value]);
      }
    }
  });

  return formattedFilters;
};

const buildSortSummary = (appliedSorts = []) =>
  appliedSorts.map((sortRule, index) => [
    `Sort ${index + 1}`,
    `${sortRule.label} (${sortRule.direction === "desc" ? "Descending" : "Ascending"})`,
  ]);

const sanitizeFileName = (filename = "report") =>
  `${filename}`.replace(/[\\/:*?"<>|]+/g, "_").trim() || "report";

const EXPORT_PROGRESS_TARGET_CHUNKS = 25;
const EXPORT_ROW_PROCESSING_PROGRESS_CAP = 80;

const yieldToBrowser = () => new Promise((resolve) => setTimeout(resolve, 0));

const buildExportPayload = async (
  data,
  headers,
  filename,
  appliedFilters = {},
  metadata = null,
  appliedSorts = [],
  onProgress,
) => {
  const baseHeaders = getFilteredHeaders(headers);
  const title = sanitizeFileName(filename);
  const metadataRows = metadata
    ? Object.entries(metadata)
        .filter(
          ([, value]) => value !== undefined && value !== null && value !== "",
        )
        .map(([key, value]) => [key, `${value}`])
    : [];
  const filterRows = buildFilterSummary(appliedFilters, baseHeaders);
  const sortRows = buildSortSummary(appliedSorts);

  const serialNumberHeader = { name: "S No", key: SERIAL_NUMBER_KEY };
  const filteredHeaders = [serialNumberHeader, ...baseHeaders];
  const headerNames = filteredHeaders.map((header) => header.name);

  const totalRows = data.length;
  const chunkSize = Math.max(
    1,
    Math.ceil(totalRows / EXPORT_PROGRESS_TARGET_CHUNKS),
  );
  const dataRows = [];
  let runningSerial = 0;
  const serialNumbers = data.map((item) =>
    SUMMARY_ROW_TYPES.has(item?.rowType) || item?.__isTotalRow
      ? ""
      : `${(runningSerial += 1)}`,
  );

  for (let start = 0; start < totalRows; start += chunkSize) {
    const chunk = data.slice(start, start + chunkSize);
    chunk.forEach((item, indexInChunk) => {
      dataRows.push([
        serialNumbers[start + indexInChunk],
        ...baseHeaders.map((header) => `${getCellValue(item, header)}`),
      ]);
    });

    if (typeof onProgress === "function") {
      const processed = Math.min(start + chunkSize, totalRows);
      onProgress(
        Math.round(
          (processed / totalRows) * EXPORT_ROW_PROCESSING_PROGRESS_CAP,
        ),
      );
    }

    await yieldToBrowser();
  }

  return {
    title,
    filteredHeaders,
    metadataRows,
    filterRows,
    sortRows,
    headerNames,
    dataRows,
    rawRows: data,
  };
};

const downloadCsvFile = (content, filename) => {
  const blob = new Blob(["\uFEFF", content], {
    type: "text/csv; charset=utf-8",
  });
  saveAs(blob, `${filename}.csv`);
};

const buildCsvContent = (exportPayload) => {
  const { metadataRows, filterRows, sortRows, headerNames, dataRows } =
    exportPayload;
  const csvRows = [];

  const pushKeyValueRows = (sectionTitle, rows) => {
    if (!rows.length) return;
    csvRows.push([sectionTitle]);
    for (let index = 0; index < rows.length; index += 2) {
      const [firstLabel, firstValue] = rows[index];
      const secondEntry = rows[index + 1];

      if (secondEntry) {
        const [secondLabel, secondValue] = secondEntry;
        csvRows.push([firstLabel, firstValue, secondLabel, secondValue]);
      } else {
        csvRows.push([firstLabel, firstValue]);
      }
    }
    csvRows.push([]);
  };

  pushKeyValueRows("REPORT DETAILS", metadataRows);
  pushKeyValueRows("APPLIED FILTERS", filterRows);
  pushKeyValueRows("APPLIED SORT", sortRows);

  csvRows.push(headerNames);
  dataRows.forEach((row) => csvRows.push(row));

  return csvRows
    .map((row) =>
      row.map((value) => `"${`${value ?? ""}`.replace(/"/g, '""')}"`).join(","),
    )
    .join("\r\n");
};

const applyThinBorder = (cell) => {
  cell.border = {
    top: { style: "thin", color: { argb: excelColors.border } },
    left: { style: "thin", color: { argb: excelColors.border } },
    bottom: { style: "thin", color: { argb: excelColors.border } },
    right: { style: "thin", color: { argb: excelColors.border } },
  };
};

const applyDataCellStyle = (cell, align = "center", fill = null) => {
  applyThinBorder(cell);
  cell.alignment = {
    vertical: "middle",
    horizontal: align,
  };
  cell.font = {
    name: "Calibri",
    size: 11,
    color: { argb: excelColors.text },
  };
  if (fill) {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: fill },
    };
  }
};

const setWorksheetCellValue = (cell, value, options = {}) => {
  const { preferInteger = false } = options;

  if (typeof value === "number" && !Number.isNaN(value)) {
    cell.value = value;
    cell.numFmt = Number.isInteger(value) || preferInteger ? "0" : "0.00";
    return;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (/^-?\d+$/.test(trimmedValue)) {
      cell.value = Number(trimmedValue);
      cell.numFmt = "0";
      return;
    }
    if (/^-?\d+\.\d+$/.test(trimmedValue)) {
      cell.value = Number(trimmedValue);
      cell.numFmt = "0.00";
      return;
    }
  }

  cell.value = value;
};

const getSemanticFillColor = ({
  isEvenRow,
  header,
  value,
  isNumeric,
  numericValue,
}) => {
  const baseFill = isEvenRow ? excelColors.rowAlt : excelColors.white;
  const normalizedHeaderName = (header?.name || "").toLowerCase();
  const normalizedHeaderKey = (header?.key || "").toLowerCase();
  const semanticKey = `${normalizedHeaderName} ${normalizedHeaderKey}`;

  if (!isNumeric || Number.isNaN(numericValue) || numericValue <= 0) {
    return baseFill;
  }

  if (
    semanticKey.includes("present") ||
    semanticKey.includes("paid leave") ||
    semanticKey.includes("planned")
  ) {
    return excelColors.success;
  }

  if (
    semanticKey.includes("permission") ||
    semanticKey.includes("late") ||
    semanticKey.includes("early") ||
    semanticKey.includes("overtime") ||
    semanticKey.includes("over time")
  ) {
    return excelColors.warning;
  }

  if (
    semanticKey.includes("missing") ||
    semanticKey.includes("absent") ||
    semanticKey.includes("unpaid leave")
  ) {
    return excelColors.danger;
  }

  if (
    semanticKey.includes("male") ||
    semanticKey.includes("female") ||
    semanticKey.includes("total")
  ) {
    return excelColors.info;
  }

  return excelColors.neutral;
};

const buildWorksheetWithExcelJs = async (exportPayload, onProgress) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "OpenAI Codex";
  workbook.created = new Date();
  workbook.modified = new Date();

  const worksheet = workbook.addWorksheet("Report", {
    properties: {
      defaultRowHeight: 18,
      tabColor: { argb: excelColors.navy },
    },
    pageSetup: {
      paperSize: 9,
      orientation: "portrait",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 1,
      margins: {
        left: 0.75,
        right: 0.75,
        top: 1,
        bottom: 1,
        header: 0.5,
        footer: 0.5,
      },
    },
    views: [{ state: "normal", showGridLines: true }],
  });

  const {
    title,
    filteredHeaders,
    metadataRows,
    filterRows,
    sortRows,
    headerNames,
    dataRows,
    rawRows,
  } = exportPayload;

  worksheet.columns = headerNames.map((headerName, columnIndex) => {
    const cellLengths = dataRows.map(
      (row) => `${row[columnIndex] || ""}`.length,
    );
    const maxLength = Math.max(headerName.length, ...cellLengths, 10);
    const dynamicWidth = maxLength + 4;

    if (columnIndex === 0)
      return { width: Math.min(Math.max(dynamicWidth, 12), 22) };
    if (columnIndex === 1)
      return { width: Math.min(Math.max(dynamicWidth, 22), 45) };
    if (columnIndex === 2)
      return { width: Math.min(Math.max(dynamicWidth, 18), 34) };

    return { width: Math.min(Math.max(dynamicWidth, 14), 28) };
  });

  const totalColumns = Math.max(headerNames.length, 6);
  const lastColumnLetter = worksheet.getColumn(totalColumns).letter;

  worksheet.mergeCells(`A1:${lastColumnLetter}1`);
  const titleCell = worksheet.getCell("A1");
  titleCell.value = title;
  titleCell.font = {
    name: "Calibri",
    size: 16,
    bold: true,
    color: { argb: excelColors.white },
  };
  titleCell.alignment = { vertical: "middle", horizontal: "center" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: excelColors.navy },
  };
  applyThinBorder(titleCell);
  worksheet.getRow(1).height = 32;

  worksheet.getRow(2).height = 24;

  worksheet.mergeCells(`A3:${lastColumnLetter}3`);
  const detailsHeaderCell = worksheet.getCell("A3");
  detailsHeaderCell.value = "REPORT DETAILS";
  detailsHeaderCell.font = {
    name: "Calibri",
    size: 11,
    bold: true,
    color: { argb: excelColors.navy },
  };
  detailsHeaderCell.alignment = { vertical: "middle", horizontal: "left" };
  detailsHeaderCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: excelColors.highlight },
  };
  applyThinBorder(detailsHeaderCell);
  worksheet.getRow(3).height = 18;

  let currentRow = 4;

  const writeMetadataEntry = (
    rowNumber,
    labelStart,
    labelWidth,
    valueWidth,
    label,
    value,
  ) => {
    const labelEnd = labelStart + labelWidth - 1;
    const valueStart = labelEnd + 1;
    const valueEnd = valueStart + valueWidth - 1;

    worksheet.mergeCells(rowNumber, labelStart, rowNumber, labelEnd);
    worksheet.mergeCells(rowNumber, valueStart, rowNumber, valueEnd);

    const labelCell = worksheet.getCell(rowNumber, labelStart);
    labelCell.value = label;
    labelCell.font = {
      name: "Calibri",
      size: 11,
      bold: true,
      color: { argb: excelColors.text },
    };
    labelCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: excelColors.lightBlue },
    };
    labelCell.alignment = { vertical: "middle", horizontal: "left" };
    applyThinBorder(labelCell);

    const valueCell = worksheet.getCell(rowNumber, valueStart);
    setWorksheetCellValue(valueCell, value);
    valueCell.font = {
      name: "Calibri",
      size: 11,
      color: { argb: excelColors.text },
    };
    valueCell.alignment = { vertical: "middle", horizontal: "left" };
    valueCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: excelColors.white },
    };
    applyThinBorder(valueCell);

    for (
      let columnIndex = labelStart + 1;
      columnIndex <= labelEnd;
      columnIndex += 1
    ) {
      const mergedLabelCell = worksheet.getCell(rowNumber, columnIndex);
      applyThinBorder(mergedLabelCell);
      mergedLabelCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: excelColors.lightBlue },
      };
    }

    for (
      let columnIndex = valueStart + 1;
      columnIndex <= valueEnd;
      columnIndex += 1
    ) {
      const mergedValueCell = worksheet.getCell(rowNumber, columnIndex);
      applyThinBorder(mergedValueCell);
      mergedValueCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: excelColors.white },
      };
    }
  };

  // 2 label cols + value cols + 2 label cols + value cols must fit within
  // totalColumns without overflowing past the table's own width — the math
  // (remainingForValues = totalColumns - 4, each value >= 1 col) only holds
  // starting at 6 columns; below that the pair layout would spill past the
  // data table beneath it, so narrower reports fall back to one pair per row.
  const METADATA_PAIR_MIN_COLUMNS = 6;

  const renderMetadataRows = (rows) => {
    if (totalColumns >= METADATA_PAIR_MIN_COLUMNS) {
      const remainingForValues = totalColumns - 4;
      const value1Width = Math.max(1, Math.ceil(remainingForValues / 2));
      const value2Width = Math.max(1, remainingForValues - value1Width);

      for (let index = 0; index < rows.length; index += 2) {
        const [firstLabel, firstValue] = rows[index];
        const secondEntry = rows[index + 1];

        writeMetadataEntry(
          currentRow,
          1,
          2,
          value1Width,
          firstLabel,
          firstValue,
        );

        if (secondEntry) {
          const [secondLabel, secondValue] = secondEntry;
          writeMetadataEntry(
            currentRow,
            3 + value1Width,
            2,
            value2Width,
            secondLabel,
            secondValue,
          );
        } else {
          for (
            let columnIndex = 3 + value1Width;
            columnIndex <= totalColumns;
            columnIndex += 1
          ) {
            const blankCell = worksheet.getCell(currentRow, columnIndex);
            applyThinBorder(blankCell);
            blankCell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: excelColors.white },
            };
          }
        }

        worksheet.getRow(currentRow).height = 20;
        currentRow += 1;
      }
    } else {
      rows.forEach(([label, value]) => {
        writeMetadataEntry(currentRow, 1, 2, totalColumns - 2, label, value);
        worksheet.getRow(currentRow).height = 20;
        currentRow += 1;
      });
    }
  };

  renderMetadataRows(metadataRows);

  const extraSections = [
    { title: "APPLIED FILTERS", rows: filterRows },
    { title: "APPLIED SORT", rows: sortRows },
  ].filter((section) => section.rows.length > 0);

  extraSections.forEach((section) => {
    worksheet.getRow(currentRow).height = 14;
    currentRow += 1;

    worksheet.mergeCells(`A${currentRow}:${lastColumnLetter}${currentRow}`);
    const sectionCell = worksheet.getCell(`A${currentRow}`);
    sectionCell.value = section.title;
    sectionCell.font = {
      name: "Calibri",
      size: 11,
      bold: true,
      color: { argb: excelColors.navy },
    };
    sectionCell.alignment = { vertical: "middle", horizontal: "left" };
    sectionCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: excelColors.highlight },
    };
    applyThinBorder(sectionCell);
    worksheet.getRow(currentRow).height = 18;
    currentRow += 1;

    renderMetadataRows(section.rows);
  });

  worksheet.getRow(currentRow).height = 24;
  currentRow += 1;

  const headerRowIndex = currentRow;
  const headerRow = worksheet.getRow(headerRowIndex);
  headerNames.forEach((headerName, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = headerName;
    cell.font = {
      name: "Calibri",
      size: 11,
      bold: true,
      color: { argb: excelColors.white },
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: excelColors.blue },
    };
    cell.alignment = {
      vertical: "middle",
      horizontal: index === 0 ? "center" : index <= 3 ? "left" : "center",
      wrapText: true,
    };
    applyThinBorder(cell);
  });
  headerRow.height = 24;
  worksheet.views = [
    {
      state: "frozen",
      xSplit: 0,
      ySplit: headerRowIndex,
      topLeftCell: `A${headerRowIndex + 1}`,
      showGridLines: true,
    },
  ];

  // Columns explicitly typed "string" (e.g. Account No) are kept as text so
  // long digit strings don't get mangled into scientific notation, but a
  // text cell that merely looks like a number makes Excel show its "Number
  // Stored as Text" warning triangle. Since the value is genuinely meant to
  // be text (not a data error), tell Excel to ignore that check for these
  // columns via the ignoredErrors directive applied in injectIgnoredErrors.
  const ignoredErrorRanges =
    dataRows.length > 0
      ? filteredHeaders
          .map((header, columnIndex) =>
            header?.type === "string"
              ? worksheet.getColumn(columnIndex + 1).letter
              : null,
          )
          .filter(Boolean)
          .map(
            (columnLetter) =>
              `${columnLetter}${headerRowIndex + 1}:${columnLetter}${headerRowIndex + dataRows.length}`,
          )
      : [];

  // Writing 15,000+ rows of per-cell styling (getCell/mergeCells/font/fill/
  // border, all synchronous ExcelJS calls) in one unbroken forEach never
  // hands the thread back to the browser — the tab shows "Page
  // Unresponsive" even though nothing is actually stuck. Chunking this the
  // same way buildGroupedWorkbook/buildLegacyAttendanceWorkbook already do
  // (yield every chunkSize rows) keeps the main thread responsive.
  const totalDataRows = dataRows.length;
  const rowChunkSize = Math.max(
    1,
    Math.ceil(totalDataRows / EXPORT_PROGRESS_TARGET_CHUNKS),
  );

  for (let rowIndex = 0; rowIndex < totalDataRows; rowIndex += 1) {
    const rowValues = dataRows[rowIndex];
    const excelRow = worksheet.getRow(headerRowIndex + 1 + rowIndex);
    const isEvenRow = rowIndex % 2 === 0;
    const rowType = rawRows?.[rowIndex]?.rowType;

    if (typeof onProgress === "function") {
      // Phase 2 (writing/styling rows) runs after buildExportPayload's phase
      // 1 already reported 0 -> EXPORT_ROW_PROCESSING_PROGRESS_CAP for the
      // same rows. Reusing that same 0 -> CAP formula here made the bar
      // visibly reset and climb to 80% a second time; this continues from
      // CAP up to 100 instead.
      onProgress(
        Math.round(
          EXPORT_ROW_PROCESSING_PROGRESS_CAP +
            ((rowIndex + 1) / totalDataRows) *
              (100 - EXPORT_ROW_PROCESSING_PROGRESS_CAP),
        ),
      );
    }

    if (rowIndex % rowChunkSize === 0) {
      await yieldToBrowser();
    }

    if (rowType === "department_header") {
      const headerRowNumber = excelRow.number;
      worksheet.mergeCells(
        `A${headerRowNumber}:${lastColumnLetter}${headerRowNumber}`,
      );
      const sectionCell = worksheet.getCell(`A${headerRowNumber}`);
      sectionCell.value = rawRows[rowIndex]?.department ?? rowValues[0];
      sectionCell.font = {
        name: "Calibri",
        size: 11,
        bold: true,
        color: { argb: excelColors.text },
      };
      sectionCell.alignment = { vertical: "middle", horizontal: "left" };
      sectionCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: excelColors.info },
      };
      applyThinBorder(sectionCell);
      for (let columnIndex = 2; columnIndex <= totalColumns; columnIndex += 1) {
        const mergedCell = worksheet.getCell(headerRowNumber, columnIndex);
        mergedCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: excelColors.info },
        };
        applyThinBorder(mergedCell);
      }
      excelRow.height = 20;
      continue;
    }

    if (rowType === "department_total") {
      rowValues.forEach((value, columnIndex) => {
        const cell = excelRow.getCell(columnIndex + 1);
        const numericValue = Number(value);
        const hasNumericValue =
          value !== "" && value !== "N/A" && !Number.isNaN(numericValue);

        if (columnIndex === 0) {
          cell.value = "";
        } else if (columnIndex === 1) {
          cell.value = "Subtotal";
        } else if (value === "N/A") {
          cell.value = "";
        } else if (hasNumericValue) {
          cell.value = numericValue;
          cell.numFmt = Number.isInteger(numericValue) ? "0" : "0.00";
        } else {
          cell.value = value;
        }

        cell.font = {
          name: "Calibri",
          size: 11,
          bold: true,
          color: { argb: excelColors.text },
        };
        cell.alignment = {
          vertical: "middle",
          horizontal:
            columnIndex === 0 ? "center" : columnIndex <= 3 ? "left" : "center",
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: excelColors.neutral },
        };
        applyThinBorder(cell);
      });
      excelRow.height = 18;
      continue;
    }

    rowValues.forEach((value, columnIndex) => {
      const cell = excelRow.getCell(columnIndex + 1);
      const header = filteredHeaders?.[columnIndex];
      const normalizedHeaderName = (header?.name || "").toLowerCase();
      const normalizedHeaderKey = (header?.key || "").toLowerCase();
      const isNumberColumn = header?.type === "number";
      const isSerialNumberColumn = header?.key === SERIAL_NUMBER_KEY;
      const isIdOrCodeColumn =
        normalizedHeaderKey.includes("employee_id") ||
        normalizedHeaderName.includes("employee id") ||
        normalizedHeaderKey.includes("user_code") ||
        normalizedHeaderName.includes("user code");
      const isPlainIntegerString =
        typeof value === "string" && /^[0-9]+$/.test(value);
      const isNumericString =
        typeof value === "string" && /^-?\d+(\.\d+)?$/.test(value.trim());
      const isExplicitStringColumn = header?.type === "string";
      // Numeric-looking values in an explicitly string-typed column (e.g.
      // "Account No") must stay text: writing them as real numbers makes
      // Excel's General format render long digit strings (14+ digits, like
      // bank account numbers) in scientific notation (e.g. "1.34601E+14").
      const isNumeric =
        !isExplicitStringColumn &&
        columnIndex >= 4 &&
        value !== "" &&
        !Number.isNaN(Number(value));

      if (isSerialNumberColumn) {
        if (value === "" || value === "N/A") {
          cell.value = "";
        } else {
          cell.value = Number(value);
          cell.numFmt = "0";
        }
      } else if (isIdOrCodeColumn && isPlainIntegerString) {
        // Excel flags text cells that merely look numeric with the
        // "number stored as text" warning (this also happens if the cell
        // format is explicitly Text). Storing a real number instead avoids
        // it entirely. Codes with letters (e.g. "VST3003") stay as plain
        // text and are never flagged since they aren't numeric-looking.
        cell.value = Number(value);
        cell.numFmt = "0";
      } else if (
        isNumberColumn &&
        (isNumericString || typeof value === "number")
      ) {
        cell.value = Number(value);
        cell.numFmt = Number.isInteger(Number(value)) ? "0" : "0.00";
      } else {
        cell.value = isNumeric ? Number(value) : value;
      }

      const numericValue = Number(value);
      const isGrandTotal = rowType === "grand_total";
      // Daily Pay Run's department total ("Subtotal") rows carry
      // `__isTotalRow` instead of `rowType: "department_total"` (see
      // activeExportData in DailyPayRun/index.js) so they skip the
      // hardcoded "Subtotal" column logic above — but they still need the
      // same neutral, bold styling as Salary Statement's subtotal rows
      // (the separate "department_header" banner row above already gets
      // its own highlighted color from the existing rowType branch).
      const isDepartmentTotalRow = Boolean(rawRows?.[rowIndex]?.__isTotalRow);
      const fillColor = isGrandTotal
        ? excelColors.grandTotal
        : isDepartmentTotalRow
          ? excelColors.neutral
          : getSemanticFillColor({
              isEvenRow,
              header,
              value,
              isNumeric,
              numericValue,
            });

      applyDataCellStyle(
        cell,
        columnIndex === 0 ? "center" : columnIndex <= 3 ? "left" : "center",
        fillColor,
      );

      if (isGrandTotal || isDepartmentTotalRow) {
        cell.font = { ...cell.font, bold: true };
      }

      if (fillColor !== excelColors.white && fillColor !== excelColors.rowAlt) {
        cell.border = {
          top: {
            style: "thin",
            color: {
              argb: isEvenRow ? excelColors.lightBlue : excelColors.border,
            },
          },
          left: {
            style: "thin",
            color: {
              argb: isEvenRow ? excelColors.lightBlue : excelColors.border,
            },
          },
          bottom: {
            style: "thin",
            color: {
              argb: isEvenRow ? excelColors.lightBlue : excelColors.border,
            },
          },
          right: {
            style: "thin",
            color: {
              argb: isEvenRow ? excelColors.lightBlue : excelColors.border,
            },
          },
        };
      }
    });

    excelRow.height = 17;
  }

  worksheet.eachRow((row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      if (!cell.font) {
        cell.font = {
          name: "Calibri",
          size: 11,
          color: { argb: excelColors.text },
        };
      }
    });
  });

  return { workbook, ignoredErrorRanges };
};

const injectIgnoredErrors = async (buffer, ranges) => {
  if (!ranges || ranges.length === 0) return buffer;

  const zip = await JSZip.loadAsync(buffer);
  const sheetPaths = Object.keys(zip.files).filter((path) =>
    /^xl\/worksheets\/sheet\d+\.xml$/.test(path),
  );
  if (sheetPaths.length === 0) return buffer;

  const ignoredErrorsXml = `<ignoredErrors><ignoredError sqref="${ranges.join(" ")}" numberStoredAsText="1"/></ignoredErrors>`;

  await Promise.all(
    sheetPaths.map(async (path) => {
      const xml = await zip.file(path).async("string");
      if (!xml.includes("</worksheet>")) return;
      // Per the OOXML schema, ignoredErrors must come after pageSetup/
      // headerFooter and before drawing/tableParts/extLst — this hook never
      // writes those trailing elements, so appending right before the
      // closing tag keeps element order valid.
      zip.file(path, xml.replace("</worksheet>", `${ignoredErrorsXml}</worksheet>`));
    }),
  );

  return zip.generateAsync({ type: "uint8array" });
};

const ReportPdfDocument = ({ exportPayload }) => {
  const {
    title,
    metadataRows,
    filterRows,
    sortRows,
    headerNames,
    dataRows,
    rawRows,
  } = exportPayload;
  const detailsRows = [...metadataRows, ...filterRows, ...sortRows];
  const pairedDetailsRows = [];
  for (let index = 0; index < detailsRows.length; index += 2) {
    pairedDetailsRows.push([
      detailsRows[index],
      detailsRows[index + 1] || null,
    ]);
  }
  const tableScale = getPdfTableScale(headerNames.length);
  const cellTextStyle = {
    fontSize: tableScale.fontSize,
    padding: tableScale.padding,
  };
  const pagePadding = headerNames.length > 16 ? 14 : 28;
  const orientation = getPdfOrientation(headerNames.length);

  return (
    <Document title={title}>
      <Page
        size="A4"
        orientation={orientation}
        style={[pdfStyles.page, { padding: pagePadding }]}
      >
        <Text style={pdfStyles.title}>{title}</Text>

        {detailsRows.length > 0 ? (
          <>
            <Text style={pdfStyles.sectionTitle}>Report Details</Text>
            <View style={pdfStyles.metadataGrid}>
              {pairedDetailsRows.map(([first, second], index) => (
                <View
                  key={`meta-row-${index}`}
                  style={[
                    pdfStyles.metadataRow,
                    index === pairedDetailsRows.length - 1
                      ? { borderBottomWidth: 0 }
                      : null,
                  ]}
                >
                  <Text style={pdfStyles.metadataLabel}>{first[0]}</Text>
                  <Text style={pdfStyles.metadataValue}>{first[1] || "-"}</Text>
                  <Text style={pdfStyles.metadataLabel}>
                    {second ? second[0] : ""}
                  </Text>
                  <Text style={pdfStyles.metadataValue}>
                    {second ? second[1] || "-" : ""}
                  </Text>
                </View>
              ))}
            </View>
          </>
        ) : null}

        <View style={pdfStyles.table}>
          <View style={pdfStyles.tableRow} fixed>
            {headerNames.map((headerName, columnIndex) => (
              <Text
                key={headerName}
                style={[
                  pdfStyles.headerCell,
                  cellTextStyle,
                  columnIndex === 0 ? pdfStyles.serialCell : null,
                ]}
              >
                {headerName}
              </Text>
            ))}
          </View>

          {dataRows.map((row, rowIndex) => {
            const rowType = rawRows?.[rowIndex]?.rowType;
            const isLastRow = rowIndex === dataRows.length - 1;

            if (rowType === "department_header") {
              return (
                <View
                  key={`row-${rowIndex}`}
                  wrap={false}
                  style={[
                    pdfStyles.tableRow,
                    { backgroundColor: "#eef5ff" },
                    isLastRow ? { borderBottomWidth: 0 } : null,
                  ]}
                >
                  <Text
                    style={[
                      pdfStyles.bodyCell,
                      cellTextStyle,
                      { fontWeight: 700 },
                    ]}
                  >
                    {rawRows[rowIndex]?.department ?? row[0]}
                  </Text>
                </View>
              );
            }

            if (rowType === "department_total") {
              return (
                <View
                  key={`row-${rowIndex}`}
                  wrap={false}
                  style={[
                    pdfStyles.tableRow,
                    { backgroundColor: "#f8fafc" },
                    isLastRow ? { borderBottomWidth: 0 } : null,
                  ]}
                >
                  {row.map((cell, cellIndex) => (
                    <Text
                      key={`${rowIndex}-${cellIndex}`}
                      style={[
                        pdfStyles.bodyCell,
                        cellTextStyle,
                        { fontWeight: 700 },
                        cellIndex === 0 ? pdfStyles.serialCell : null,
                      ]}
                    >
                      {cellIndex === 0
                        ? ""
                        : cellIndex === 1
                          ? "Subtotal"
                          : cell === "N/A"
                            ? ""
                            : cell || ""}
                    </Text>
                  ))}
                </View>
              );
            }

            const isGrandTotal = rowType === "grand_total";

            return (
              <View
                key={`row-${rowIndex}`}
                wrap={false}
                style={[
                  pdfStyles.tableRow,
                  isGrandTotal
                    ? { backgroundColor: "#f4f8ff" }
                    : rowIndex % 2 === 0
                      ? pdfStyles.evenRow
                      : null,
                  isLastRow ? { borderBottomWidth: 0 } : null,
                ]}
              >
                {row.map((cell, cellIndex) => (
                  <Text
                    key={`${rowIndex}-${cellIndex}`}
                    style={[
                      pdfStyles.bodyCell,
                      cellTextStyle,
                      isGrandTotal ? { fontWeight: 700 } : null,
                      cellIndex === 0 ? pdfStyles.serialCell : null,
                    ]}
                  >
                    {isGrandTotal ? cell || "" : cell || "-"}
                  </Text>
                ))}
              </View>
            );
          })}
        </View>
      </Page>
    </Document>
  );
};

// Grouped export: renders a report as repeated per-group sections (a one-row
// "summary" mini-table followed by its own multi-row "detail" mini-table),
// mirroring a master/detail UI (e.g. Detailed Working Hours' per-employee row
// + its per-day expand panel) instead of flattening everything into one
// table. Additive to the single-table exporters above — nothing here changes
// their behavior.
const TOTAL_BEARING_DETAIL_KEYS = new Set([
  "over_time",
  "working_hours",
  "leave_credit_hours",
  "total_hours",
]);

// Every grouped mini-table gets its own leading "S No" column (summary rows
// number the group/employee position; detail rows number the day position
// within that group) — matching the auto-added serial column every other
// (non-grouped) export in this file already has.
const GROUPED_SERIAL_KEY = "__grouped_serial__";
const GROUPED_SERIAL_HEADER = { name: "S No", key: GROUPED_SERIAL_KEY };

const withSerialHeader = (headers) =>
  headers.length > 0 ? [GROUPED_SERIAL_HEADER, ...headers] : headers;

const getGroupedCellValue = (row, header, serialValue) =>
  header.key === GROUPED_SERIAL_KEY ? serialValue : getCellValue(row, header);

// Distributes `totalColumns` physical spreadsheet columns evenly across
// `columnCount` logical headers, so a mini-table with fewer real columns
// (e.g. the detail section next to a wider summary section) stretches its
// own columns to fill the same total width instead of stopping partway
// across the sheet with empty filler cells after it — mirroring how the PDF
// version already looks, since its cells use flex:1 and auto-stretch.
const computeColumnSpans = (columnCount, totalColumns) => {
  if (columnCount <= 0) return [];
  const baseSpan = Math.floor(totalColumns / columnCount);
  const remainder = totalColumns - baseSpan * columnCount;
  return Array.from({ length: columnCount }, (_, index) =>
    baseSpan + (index < remainder ? 1 : 0),
  );
};

const getColumnStarts = (spans) => {
  const starts = [];
  let cursor = 1;
  spans.forEach((span) => {
    starts.push(cursor);
    cursor += span;
  });
  return starts;
};

// Writes one row's cells at their computed (possibly multi-column-wide)
// positions, merging where a header's span is more than one physical column.
// Only the merge's anchor (top-left) cell is styled — ExcelJS/Excel renders
// a merged range using its anchor cell's fill/border, same convention this
// file already relies on for the title and metadata rows above.
const writeSpannedRow = (worksheet, rowNumber, headers, starts, spans, cellBuilder) => {
  headers.forEach((header, index) => {
    const startColumn = starts[index];
    const span = spans[index];
    if (span > 1) {
      worksheet.mergeCells(
        rowNumber,
        startColumn,
        rowNumber,
        startColumn + span - 1,
      );
    }
    const cell = worksheet.getCell(rowNumber, startColumn);
    cellBuilder(cell, header, index);
  });
};

const buildGroupedWorkbook = async (
  groups,
  summaryHeaders,
  detailHeaders,
  filename,
  metadata,
  onProgress,
) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "OpenAI Codex";
  workbook.created = new Date();
  workbook.modified = new Date();

  const filteredSummaryHeaders = withSerialHeader(
    getFilteredHeaders(summaryHeaders),
  );
  // No "S No" for the expand/detail section — only the parent summary rows
  // get numbered.
  const filteredDetailHeaders = getFilteredHeaders(detailHeaders);
  const totalColumns = Math.max(
    filteredSummaryHeaders.length,
    filteredDetailHeaders.length,
    3,
  );

  const summarySpans = computeColumnSpans(
    filteredSummaryHeaders.length,
    totalColumns,
  );
  const summaryStarts = getColumnStarts(summarySpans);
  const detailSpans = computeColumnSpans(
    filteredDetailHeaders.length,
    totalColumns,
  );
  const detailStarts = getColumnStarts(detailSpans);

  const worksheet = workbook.addWorksheet("Report", {
    properties: {
      defaultRowHeight: 18,
      tabColor: { argb: excelColors.navy },
    },
    pageSetup: {
      paperSize: 9,
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      margins: {
        left: 0.5,
        right: 0.5,
        top: 0.75,
        bottom: 0.75,
        header: 0.3,
        footer: 0.3,
      },
    },
    views: [{ state: "normal", showGridLines: true }],
  });

  worksheet.columns = Array.from({ length: totalColumns }, () => ({
    width: 20,
  }));
  const lastColumnLetter = worksheet.getColumn(totalColumns).letter;

  const title = sanitizeFileName(filename);
  worksheet.mergeCells(`A1:${lastColumnLetter}1`);
  const titleCell = worksheet.getCell("A1");
  titleCell.value = title;
  titleCell.font = {
    name: "Calibri",
    size: 16,
    bold: true,
    color: { argb: excelColors.white },
  };
  titleCell.alignment = { vertical: "middle", horizontal: "center" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: excelColors.navy },
  };
  applyThinBorder(titleCell);
  worksheet.getRow(1).height = 32;

  let currentRow = 3;

  const metadataEntries = metadata
    ? Object.entries(metadata).filter(
        ([, value]) => value !== undefined && value !== null && value !== "",
      )
    : [];

  if (metadataEntries.length > 0) {
    metadataEntries.forEach(([label, value]) => {
      worksheet.mergeCells(currentRow, 1, currentRow, 2);
      worksheet.mergeCells(currentRow, 3, currentRow, totalColumns);
      const labelCell = worksheet.getCell(currentRow, 1);
      labelCell.value = label;
      labelCell.font = { name: "Calibri", size: 11, bold: true };
      labelCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: excelColors.lightBlue },
      };
      applyThinBorder(labelCell);
      const valueCell = worksheet.getCell(currentRow, 3);
      setWorksheetCellValue(valueCell, `${value}`);
      applyThinBorder(valueCell);
      currentRow += 1;
    });
    currentRow += 1;
  }

  const totalGroups = groups.length;
  const chunkSize = Math.max(
    1,
    Math.ceil(totalGroups / EXPORT_PROGRESS_TARGET_CHUNKS),
  );

  for (let index = 0; index < groups.length; index += 1) {
    const group = groups[index];

    writeSpannedRow(
      worksheet,
      currentRow,
      filteredSummaryHeaders,
      summaryStarts,
      summarySpans,
      (cell, header) => {
        cell.value = header.name;
        cell.font = {
          name: "Calibri",
          size: 11,
          bold: true,
          color: { argb: excelColors.white },
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: excelColors.blue },
        };
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        applyThinBorder(cell);
      },
    );
    currentRow += 1;

    writeSpannedRow(
      worksheet,
      currentRow,
      filteredSummaryHeaders,
      summaryStarts,
      summarySpans,
      (cell, header) => {
        setWorksheetCellValue(
          cell,
          getGroupedCellValue(group.summaryRow, header, index + 1),
        );
        applyDataCellStyle(cell, "center", excelColors.paleBlue);
      },
    );
    currentRow += 2;

    if (filteredDetailHeaders.length > 0) {
      writeSpannedRow(
        worksheet,
        currentRow,
        filteredDetailHeaders,
        detailStarts,
        detailSpans,
        (cell, header) => {
          cell.value = header.name;
          cell.font = {
            name: "Calibri",
            size: 11,
            bold: true,
            color: { argb: excelColors.white },
          };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: excelColors.blue },
          };
          cell.alignment = {
            vertical: "middle",
            horizontal: "center",
            wrapText: true,
          };
          applyThinBorder(cell);
        },
      );
      currentRow += 1;

      const detailRowsList = group.detailRows || [];
      for (let rowIndex = 0; rowIndex < detailRowsList.length; rowIndex += 1) {
        const detailRow = detailRowsList[rowIndex];
        const rowFill =
          rowIndex % 2 === 0 ? excelColors.white : excelColors.rowAlt;
        writeSpannedRow(
          worksheet,
          currentRow,
          filteredDetailHeaders,
          detailStarts,
          detailSpans,
          (cell, header) => {
            setWorksheetCellValue(
              cell,
              getGroupedCellValue(detailRow, header, rowIndex + 1),
            );
            applyDataCellStyle(cell, "center", rowFill);
          },
        );
        currentRow += 1;
      }

      if (group.totals) {
        writeSpannedRow(
          worksheet,
          currentRow,
          filteredDetailHeaders,
          detailStarts,
          detailSpans,
          (cell, header, columnIndex) => {
            // applyDataCellStyle must run BEFORE the bold override below —
            // it unconditionally overwrites cell.font, so setting bold
            // first (as this used to) got silently clobbered and the Total
            // row rendered in the normal (non-bold) weight.
            applyDataCellStyle(cell, "center", excelColors.neutral);
            // No S.No column here to anchor "Total" to, so the label always
            // takes the detail section's first column regardless of which
            // real header ended up there (matches the pre-S.No convention).
            if (columnIndex === 0) {
              cell.value = "Total";
              cell.font = { ...cell.font, bold: true };
            } else if (TOTAL_BEARING_DETAIL_KEYS.has(header.key)) {
              setWorksheetCellValue(cell, group.totals[header.key]);
              cell.font = { ...cell.font, bold: true };
            }
          },
        );
        currentRow += 1;
      }
    }

    currentRow += 1;

    if (typeof onProgress === "function") {
      onProgress(
        Math.round(
          ((index + 1) / totalGroups) * EXPORT_ROW_PROCESSING_PROGRESS_CAP,
        ),
      );
    }

    if (index % chunkSize === 0) {
      await yieldToBrowser();
    }
  }

  return workbook;
};

const GroupedReportPdfDocument = ({
  groups,
  summaryHeaders,
  detailHeaders,
  filename,
  metadata,
  orientationOverride,
}) => {
  const summaryHeadersWithSerial = withSerialHeader(summaryHeaders);
  // No "S No" for the expand/detail section — only the parent summary rows
  // get numbered.
  const title = sanitizeFileName(filename);
  // Page scale/orientation/width checks are all based on the parent
  // (summary) header count only — the expand/detail section is intentionally
  // not considered here (per report requirements), so a wide detail table
  // never blocks or shrinks the export on its own.
  const columnCount = Math.max(summaryHeadersWithSerial.length, 3);
  const tableScale = getPdfTableScale(columnCount);
  const cellTextStyle = {
    fontSize: tableScale.fontSize,
    padding: tableScale.padding,
  };
  const pagePadding = columnCount > 16 ? 14 : 28;
  // A caller can force landscape regardless of the summary column count —
  // e.g. Punch/Adjustment Detailed, whose per-punch detail rows always read
  // better wide even when the identity columns alone would fit portrait.
  const orientation = orientationOverride || getPdfOrientation(columnCount);

  const metadataRows = metadata
    ? Object.entries(metadata)
        .filter(
          ([, value]) => value !== undefined && value !== null && value !== "",
        )
        .map(([key, value]) => [key, `${value}`])
    : [];

  return (
    <Document title={title}>
      <Page
        size="A4"
        orientation={orientation}
        style={[pdfStyles.page, { padding: pagePadding }]}
      >
        <Text style={pdfStyles.title}>{title}</Text>

        {metadataRows.length > 0 ? (
          <View style={pdfStyles.metadataGrid}>
            {metadataRows.map(([label, value], index) => (
              <View
                key={`meta-row-${index}`}
                style={[
                  pdfStyles.metadataRow,
                  index === metadataRows.length - 1
                    ? { borderBottomWidth: 0 }
                    : null,
                ]}
              >
                <Text style={pdfStyles.metadataLabel}>{label}</Text>
                <Text style={pdfStyles.metadataValue}>{value || "-"}</Text>
              </View>
            ))}
          </View>
        ) : null}

        {groups.map((group, groupIndex) => (
          // Not wrap={false} here — a group with enough detail rows can be
          // taller than a single page, and forcing an over-height block to
          // stay unbroken is what corrupted the layout (react-pdf logs
          // "Node of type VIEW can't wrap between pages and it's bigger than
          // available page height" and then collapses/overlaps alternating
          // rows instead of paginating). Only the small, fixed-size summary
          // table below is pinned together, so its header can never be
          // orphaned from its one data row; the detail table is left free to
          // break across pages like any normal table.
          <View key={`group-${groupIndex}`} style={{ marginBottom: 12 }}>
            <View style={pdfStyles.table} wrap={false}>
              <View style={pdfStyles.tableRow}>
                {summaryHeadersWithSerial.map((header) => (
                  <Text
                    key={header.key}
                    style={[pdfStyles.headerCell, cellTextStyle]}
                  >
                    {header.name}
                  </Text>
                ))}
              </View>
              <View style={[pdfStyles.tableRow, { borderBottomWidth: 0 }]}>
                {summaryHeadersWithSerial.map((header) => (
                  <Text
                    key={header.key}
                    style={[pdfStyles.bodyCell, cellTextStyle]}
                  >
                    {`${getGroupedCellValue(group.summaryRow, header, groupIndex + 1)}`}
                  </Text>
                ))}
              </View>
            </View>

            {detailHeaders.length > 0 ? (
              <View style={[pdfStyles.table, { marginTop: 6 }]}>
                <View style={pdfStyles.tableRow}>
                  {detailHeaders.map((header) => (
                    <Text
                      key={header.key}
                      style={[pdfStyles.headerCell, cellTextStyle]}
                    >
                      {header.name}
                    </Text>
                  ))}
                </View>
                {(group.detailRows || []).map((detailRow, rowIndex) => (
                  <View
                    key={`detail-row-${rowIndex}`}
                    style={[
                      pdfStyles.tableRow,
                      rowIndex % 2 === 0 ? pdfStyles.evenRow : null,
                      !group.totals &&
                      rowIndex === (group.detailRows || []).length - 1
                        ? { borderBottomWidth: 0 }
                        : null,
                    ]}
                  >
                    {detailHeaders.map((header) => (
                      <Text
                        key={header.key}
                        style={[pdfStyles.bodyCell, cellTextStyle]}
                      >
                        {`${getCellValue(detailRow, header)}`}
                      </Text>
                    ))}
                  </View>
                ))}
                {group.totals ? (
                  <View
                    style={[pdfStyles.tableRow, { borderBottomWidth: 0 }]}
                  >
                    {detailHeaders.map((header, columnIndex) => (
                      <Text
                        key={header.key}
                        style={[
                          pdfStyles.bodyCell,
                          cellTextStyle,
                          { fontWeight: 700 },
                        ]}
                      >
                        {columnIndex === 0
                          ? "Total"
                          : TOTAL_BEARING_DETAIL_KEYS.has(header.key)
                            ? `${group.totals[header.key]}`
                            : ""}
                      </Text>
                    ))}
                  </View>
                ) : null}
              </View>
            ) : null}
          </View>
        ))}
      </Page>
    </Document>
  );
};

const buildGroupedCsvContent = (groups, summaryHeaders, detailHeaders, metadata) => {
  const filteredSummaryHeaders = withSerialHeader(getFilteredHeaders(summaryHeaders));
  // No "S No" for the expand/detail section — only the parent summary rows
  // get numbered.
  const filteredDetailHeaders = getFilteredHeaders(detailHeaders);
  const csvRows = [];

  const metadataEntries = metadata
    ? Object.entries(metadata).filter(
        ([, value]) => value !== undefined && value !== null && value !== "",
      )
    : [];

  if (metadataEntries.length > 0) {
    csvRows.push(["REPORT DETAILS"]);
    metadataEntries.forEach(([label, value]) => {
      csvRows.push([label, `${value}`]);
    });
    csvRows.push([]);
  }

  groups.forEach((group, groupIndex) => {
    csvRows.push(filteredSummaryHeaders.map((header) => header.name));
    csvRows.push(
      filteredSummaryHeaders.map(
        (header) =>
          `${getGroupedCellValue(group.summaryRow, header, groupIndex + 1)}`,
      ),
    );
    csvRows.push([]);

    if (filteredDetailHeaders.length > 0) {
      csvRows.push(filteredDetailHeaders.map((header) => header.name));
      (group.detailRows || []).forEach((detailRow) => {
        csvRows.push(
          filteredDetailHeaders.map(
            (header) => `${getCellValue(detailRow, header)}`,
          ),
        );
      });

      if (group.totals) {
        csvRows.push(
          filteredDetailHeaders.map((header, columnIndex) => {
            if (columnIndex === 0) return "Total";
            return TOTAL_BEARING_DETAIL_KEYS.has(header.key)
              ? `${group.totals[header.key]}`
              : "";
          }),
        );
      }
    }
    csvRows.push([]);
  });

  return csvRows
    .map((row) =>
      row.map((value) => `"${`${value ?? ""}`.replace(/"/g, '""')}"`).join(","),
    )
    .join("\r\n");
};

const LEGACY_ATTENDANCE_SHEET_NAME = "All Employees";

const getLegacyAttendanceDetailLabel = (header) => {
  switch (header?.key) {
    case "Attendance_Status":
      return "Attendance";
    case "Shift_Code":
      return "Shift Name";
    default:
      return header?.name || "";
  }
};

const buildLegacyAttendanceBlocks = (groups, summaryHeaders, detailHeaders) =>
  (groups || []).map((group) => {
    const summaryRow = group?.summaryRow || {};
    const detailRows = group?.detailRows || [];
    const dateHeader = detailHeaders.find((header) => header.key === "Date");
    const matrixHeaders = detailHeaders.filter((header) => header.key !== "Date");

    return {
      employeeName:
        summaryRow.Name || summaryRow.employee_name || summaryRow["Employee Name"] || "-",
      employeeId:
        summaryRow.employee_id || summaryRow["Employee Id"] || summaryRow["Employee ID"] || "",
      department:
        summaryRow.Dept || summaryRow.department_name || summaryRow.Department || "-",
      summaryItems: summaryHeaders
        .filter(
          (header) =>
            !["Name", "employee_id", "Dept"].includes(header.key),
        )
        .map((header) => ({
          label: header.name,
          value: getCellValue(summaryRow, header),
        })),
      detailMatrix: {
        title: "Details",
        dates: detailRows.map((row) =>
          dateHeader ? getCellValue(row, dateHeader) : "-",
        ),
        rows: matrixHeaders.map((header) => ({
          label: getLegacyAttendanceDetailLabel(header),
          values: detailRows.map((row) => getCellValue(row, header)),
        })),
      },
    };
  });

const buildLegacyAttendanceWorkbook = async (
  groups,
  summaryHeaders,
  detailHeaders,
  filename,
  onProgress,
) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "OpenAI Codex";
  workbook.created = new Date();
  workbook.modified = new Date();

  const worksheet = workbook.addWorksheet(LEGACY_ATTENDANCE_SHEET_NAME, {
    properties: {
      defaultRowHeight: 18,
    },
    views: [{ state: "normal", showGridLines: true }],
  });

  const blocks = buildLegacyAttendanceBlocks(
    groups,
    getFilteredHeaders(summaryHeaders),
    getFilteredHeaders(detailHeaders),
  );
  const maxDetailColumns = Math.max(
    8,
    ...blocks.map((block) => 1 + (block.detailMatrix?.dates?.length || 0)),
  );

  worksheet.columns = Array.from({ length: maxDetailColumns }, (_, index) => ({
    width: index === 0 ? 28.25 : 12.25,
  }));

  let currentRow = 1;
  const totalBlocks = blocks.length;
  const chunkSize = Math.max(
    1,
    Math.ceil(totalBlocks / EXPORT_PROGRESS_TARGET_CHUNKS),
  );

  const styleLegacyCell = (cell, options = {}) => {
    const {
      bold = false,
      fill = null,
      border = false,
      wrapText = false,
      horizontal = "left",
    } = options;
    cell.font = { name: "Calibri", size: 11, bold };
    cell.alignment = {
      vertical: "middle",
      horizontal,
      wrapText,
    };
    if (fill) {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: fill },
      };
    }
    if (border) {
      applyThinBorder(cell);
    }
  };

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];

    worksheet.getCell(currentRow, 1).value = `Employee: ${block.employeeName}`;
    styleLegacyCell(worksheet.getCell(currentRow, 1), { bold: true });
    currentRow += 1;

    if (block.employeeId) {
      worksheet.getCell(currentRow, 1).value = `Employee Id: ${block.employeeId}`;
      styleLegacyCell(worksheet.getCell(currentRow, 1), { bold: true });
      currentRow += 1;
    }

    worksheet.getCell(currentRow, 1).value = `Department: ${block.department}`;
    styleLegacyCell(worksheet.getCell(currentRow, 1), { bold: true });
    currentRow += 2;

    worksheet.getCell(currentRow, 1).value = "Summary";
    styleLegacyCell(worksheet.getCell(currentRow, 1), {
      bold: true,
      fill: excelColors.highlight,
    });
    currentRow += 1;

    for (
      let summaryIndex = 0;
      summaryIndex < block.summaryItems.length;
      summaryIndex += 1
    ) {
      const { label, value } = block.summaryItems[summaryIndex];
      const labelCell = worksheet.getCell(currentRow, 1);
      const valueCell = worksheet.getCell(currentRow, 2);
      labelCell.value = label;
      setWorksheetCellValue(valueCell, value);
      styleLegacyCell(labelCell, {
        bold: true,
        border: true,
        fill: excelColors.paleBlue,
      });
      styleLegacyCell(valueCell, {
        border: true,
        fill: excelColors.white,
      });
      currentRow += 1;
    }

    currentRow += 2;

    const { detailMatrix } = block;
    if (detailMatrix.rows.length > 0) {
      worksheet.getCell(currentRow, 1).value = detailMatrix.title;
      styleLegacyCell(worksheet.getCell(currentRow, 1), {
        bold: true,
        border: true,
        fill: excelColors.blue,
      });
      for (
        let dateIndex = 0;
        dateIndex < detailMatrix.dates.length;
        dateIndex += 1
      ) {
        const date = detailMatrix.dates[dateIndex];
        const cell = worksheet.getCell(currentRow, dateIndex + 2);
        cell.value = date;
        styleLegacyCell(cell, {
          bold: true,
          border: true,
          fill: excelColors.blue,
          horizontal: "center",
        });
      }
      currentRow += 1;

      for (
        let detailRowIndex = 0;
        detailRowIndex < detailMatrix.rows.length;
        detailRowIndex += 1
      ) {
        const detailRow = detailMatrix.rows[detailRowIndex];
        const labelCell = worksheet.getCell(currentRow, 1);
        labelCell.value = detailRow.label;
        const rowFill =
          detailRowIndex % 2 === 0 ? excelColors.white : excelColors.rowAlt;
        styleLegacyCell(labelCell, {
          bold: true,
          border: true,
          fill: rowFill,
        });
        for (
          let valueIndex = 0;
          valueIndex < detailRow.values.length;
          valueIndex += 1
        ) {
          const value = detailRow.values[valueIndex];
          const cell = worksheet.getCell(currentRow, valueIndex + 2);
          setWorksheetCellValue(cell, value);
          styleLegacyCell(cell, {
            border: true,
            fill: rowFill,
            horizontal: "center",
          });
        }
        currentRow += 1;
      }
    }

    currentRow += 2;
    worksheet.getCell(currentRow, 1).value = "------------------------------------";
    styleLegacyCell(worksheet.getCell(currentRow, 1), {
      bold: true,
    });
    currentRow += 2;

    if (typeof onProgress === "function") {
      onProgress(
        Math.round(
          ((index + 1) / totalBlocks) * EXPORT_ROW_PROCESSING_PROGRESS_CAP,
        ),
      );
    }

    if (index % chunkSize === 0) {
      await yieldToBrowser();
    }
  }

  return workbook;
};

const buildLegacyAttendanceCsvContent = (groups, summaryHeaders, detailHeaders) => {
  const blocks = buildLegacyAttendanceBlocks(
    groups,
    getFilteredHeaders(summaryHeaders),
    getFilteredHeaders(detailHeaders),
  );
  const csvRows = [];

  blocks.forEach((block) => {
    csvRows.push([`Employee: ${block.employeeName}`]);
    if (block.employeeId) {
      csvRows.push([`Employee Id: ${block.employeeId}`]);
    }
    csvRows.push([`Department: ${block.department}`]);
    csvRows.push([]);
    csvRows.push(["Summary"]);
    block.summaryItems.forEach(({ label, value }) => {
      csvRows.push([label, `${value ?? ""}`]);
    });
    csvRows.push([]);
    if (block.detailMatrix.rows.length > 0) {
      csvRows.push(["Details", ...block.detailMatrix.dates]);
      block.detailMatrix.rows.forEach((row) => {
        csvRows.push([row.label, ...row.values]);
      });
    }
    csvRows.push([]);
    csvRows.push(["------------------------------------"]);
    csvRows.push([]);
  });

  return csvRows
    .map((row) =>
      row.map((value) => `"${`${value ?? ""}`.replace(/"/g, '""')}"`).join(","),
    )
    .join("\r\n");
};

const LegacyAttendancePdfDocument = ({ groups, summaryHeaders, detailHeaders, filename }) => {
  const blocks = buildLegacyAttendanceBlocks(
    groups,
    getFilteredHeaders(summaryHeaders),
    getFilteredHeaders(detailHeaders),
  );
  const maxDates = Math.max(0, ...blocks.map((block) => block.detailMatrix.dates.length));
  const orientation = maxDates > 5 ? "landscape" : "portrait";
  const labelWidth = maxDates > 5 ? "22%" : "28%";
  const valueWidth = maxDates > 5 ? `${78 / Math.max(maxDates, 1)}%` : `${72 / Math.max(maxDates, 1)}%`;

  return (
    <Document title={sanitizeFileName(filename)}>
      <Page size="A4" orientation={orientation} style={pdfStyles.page}>
        {blocks.map((block, blockIndex) => (
          <View
            key={`attendance-block-${blockIndex}`}
            style={{ marginBottom: 16 }}
            wrap={false}
          >
            <Text style={[pdfStyles.bodyCell, { padding: 0, marginBottom: 2 }]}>
              {`Employee: ${block.employeeName}`}
            </Text>
            {block.employeeId ? (
              <Text style={[pdfStyles.bodyCell, { padding: 0, marginBottom: 2 }]}>
                {`Employee Id: ${block.employeeId}`}
              </Text>
            ) : null}
            <Text style={[pdfStyles.bodyCell, { padding: 0, marginBottom: 8 }]}>
              {`Department: ${block.department}`}
            </Text>

            <Text style={pdfStyles.sectionTitle}>Summary</Text>
            <View style={pdfStyles.table}>
              {block.summaryItems.map((item, index) => (
                <View
                  key={`summary-item-${index}`}
                  style={[
                    pdfStyles.tableRow,
                    index === block.summaryItems.length - 1 ? { borderBottomWidth: 0 } : null,
                  ]}
                >
                  <Text style={[pdfStyles.bodyCell, { width: "38%", fontWeight: 700 }]}>
                    {item.label}
                  </Text>
                  <Text style={[pdfStyles.bodyCell, { width: "62%" }]}>{`${item.value ?? ""}`}</Text>
                </View>
              ))}
            </View>

            {block.detailMatrix.rows.length > 0 ? (
              <>
                <Text style={pdfStyles.sectionTitle}>Details</Text>
                <View style={pdfStyles.table}>
                  <View style={pdfStyles.tableRow}>
                    <Text style={[pdfStyles.headerCell, { width: labelWidth }]}>Details</Text>
                    {block.detailMatrix.dates.map((date, dateIndex) => (
                      <Text
                        key={`date-${blockIndex}-${dateIndex}`}
                        style={[pdfStyles.headerCell, { width: valueWidth }]}
                      >
                        {date}
                      </Text>
                    ))}
                  </View>
                  {block.detailMatrix.rows.map((row, rowIndex) => (
                    <View
                      key={`detail-row-${blockIndex}-${rowIndex}`}
                      style={[
                        pdfStyles.tableRow,
                        rowIndex % 2 === 1 ? pdfStyles.evenRow : null,
                        rowIndex === block.detailMatrix.rows.length - 1
                          ? { borderBottomWidth: 0 }
                          : null,
                      ]}
                    >
                      <Text style={[pdfStyles.bodyCell, { width: labelWidth, fontWeight: 700 }]}>
                        {row.label}
                      </Text>
                      {row.values.map((value, valueIndex) => (
                        <Text
                          key={`detail-value-${blockIndex}-${rowIndex}-${valueIndex}`}
                          style={[pdfStyles.bodyCell, { width: valueWidth }]}
                        >
                          {`${value ?? ""}`}
                        </Text>
                      ))}
                    </View>
                  ))}
                </View>
              </>
            ) : null}
          </View>
        ))}
      </Page>
    </Document>
  );
};

export const useExcelDownload = ({ maxPdfRows = MAX_PDF_ROWS } = {}) => {
  const downloadCsv = useCallback(
    async (
      data,
      headers,
      filename = "report",
      appliedFilters = {},
      metadata = null,
      appliedSorts = [],
      onProgress,
    ) => {
      if (!data || data.length === 0) {
        showToast("error", "No data to download.");
        return;
      }

      try {
        const exportPayload = await buildExportPayload(
          data,
          headers,
          filename,
          appliedFilters,
          metadata,
          appliedSorts,
          onProgress,
        );
        const csvContent = buildCsvContent(exportPayload);
        downloadCsvFile(csvContent, exportPayload.title);
        if (typeof onProgress === "function") onProgress(100);
        showToast("success", "Report downloaded.");
      } catch (_error) {
        console.error("[UseExcelDownload] csv export failed:", _error);
        showToast(
          "error",
          "Couldn't download the CSV report. Please try again.",
        );
      }
    },
    [],
  );

  const downloadExcel = useCallback(
    async (
      data,
      headers,
      filename = "report",
      appliedFilters = {},
      metadata = null,
      appliedSorts = [],
      onProgress,
    ) => {
      if (!data || data.length === 0) {
        showToast("error", "No data to download.");
        return;
      }

      try {
        const exportPayload = await buildExportPayload(
          data,
          headers,
          filename,
          appliedFilters,
          metadata,
          appliedSorts,
          onProgress,
        );
        const { workbook, ignoredErrorRanges } =
          await buildWorksheetWithExcelJs(exportPayload, onProgress);
        const rawBuffer = await workbook.xlsx.writeBuffer();
        const buffer = await injectIgnoredErrors(rawBuffer, ignoredErrorRanges);
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(blob, `${exportPayload.title}.xlsx`);
        if (typeof onProgress === "function") onProgress(100);
        showToast("success", "Report downloaded.");
      } catch (_error) {
        console.error("[UseExcelDownload] operation failed:", _error);
        showToast("error", "Couldn't download the report. Please try again.");
      }
    },
    [],
  );

  const downloadPdf = useCallback(
    async (
      data,
      headers,
      filename = "report",
      appliedFilters = {},
      metadata = null,
      appliedSorts = [],
      onProgress,
    ) => {
      if (!data || data.length === 0) {
        showToast("error", "No data to download.");
        return;
      }

      if (data.length > maxPdfRows) {
        showToast(
          "error",
          `PDF supports up to ${maxPdfRows} rows. This report has ${data.length} rows — please download it as Excel instead.`,
        );
        return;
      }

      try {
        const exportPayload = await buildExportPayload(
          data,
          headers,
          filename,
          appliedFilters,
          metadata,
          appliedSorts,
          onProgress,
        );

        if (exportPayload.filteredHeaders.length > MAX_PDF_COLUMNS) {
          showToast(
            "error",
            `PDF supports up to ${MAX_PDF_COLUMNS} columns. This report has ${exportPayload.filteredHeaders.length} columns — please download it as Excel instead.`,
          );
          return;
        }

        const blob = await pdf(
          <ReportPdfDocument exportPayload={exportPayload} />,
        ).toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `${exportPayload.title}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 100);

        if (typeof onProgress === "function") onProgress(100);
        showToast("success", "Report downloaded.");
      } catch (_error) {
        console.error("[UseExcelDownload] pdf export failed:", _error);
        showToast(
          "error",
          "Couldn't download the PDF report. Please try again.",
        );
      }
    },
    [maxPdfRows],
  );

  const downloadGroupedExcel = useCallback(
    async (
      groups,
      summaryHeaders,
      detailHeaders,
      filename = "report",
      metadata = null,
      onProgress,
    ) => {
      if (!groups || groups.length === 0) {
        showToast("error", "No data to download.");
        return;
      }

      try {
        const workbook = await buildGroupedWorkbook(
          groups,
          summaryHeaders,
          detailHeaders,
          filename,
          metadata,
          onProgress,
        );
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(blob, `${sanitizeFileName(filename)}.xlsx`);
        if (typeof onProgress === "function") onProgress(100);
        showToast("success", "Report downloaded.");
      } catch (_error) {
        console.error(
          "[UseExcelDownload] grouped excel export failed:",
          _error,
        );
        showToast("error", "Couldn't download the report. Please try again.");
      }
    },
    [],
  );

  const downloadGroupedCsv = useCallback(
    async (
      groups,
      summaryHeaders,
      detailHeaders,
      filename = "report",
      metadata = null,
      onProgress,
    ) => {
      if (!groups || groups.length === 0) {
        showToast("error", "No data to download.");
        return;
      }

      try {
        const csvContent = buildGroupedCsvContent(
          groups,
          summaryHeaders,
          detailHeaders,
          metadata,
        );
        downloadCsvFile(csvContent, sanitizeFileName(filename));
        if (typeof onProgress === "function") onProgress(100);
        showToast("success", "Report downloaded.");
      } catch (_error) {
        console.error(
          "[UseExcelDownload] grouped csv export failed:",
          _error,
        );
        showToast(
          "error",
          "Couldn't download the CSV report. Please try again.",
        );
      }
    },
    [],
  );

  const downloadGroupedPdf = useCallback(
    async (
      groups,
      summaryHeaders,
      detailHeaders,
      filename = "report",
      metadata = null,
      onProgress,
      orientationOverride = null,
    ) => {
      if (!groups || groups.length === 0) {
        showToast("error", "No data to download.");
        return;
      }

      const totalRows = groups.reduce(
        (sum, group) => sum + 1 + (group.detailRows?.length || 0),
        0,
      );
      if (totalRows > MAX_PDF_ROWS) {
        showToast(
          "error",
          `PDF supports up to ${MAX_PDF_ROWS} rows. This report has ${totalRows} rows — please download it as Excel instead.`,
        );
        return;
      }

      const filteredSummaryHeaders = getFilteredHeaders(summaryHeaders);
      const filteredDetailHeaders = getFilteredHeaders(detailHeaders);
      // Only the parent (summary) header count gates the PDF column limit —
      // the expand/detail section is deliberately excluded, per report
      // requirements. +1 accounts for the auto-added "S No" column.
      const columnCount = filteredSummaryHeaders.length + 1;
      if (columnCount > MAX_PDF_COLUMNS) {
        showToast(
          "error",
          `PDF supports up to ${MAX_PDF_COLUMNS} columns. This report has ${columnCount} columns — please download it as Excel instead.`,
        );
        return;
      }

      try {
        const blob = await pdf(
          <GroupedReportPdfDocument
            groups={groups}
            summaryHeaders={filteredSummaryHeaders}
            detailHeaders={filteredDetailHeaders}
            filename={filename}
            metadata={metadata}
            orientationOverride={orientationOverride}
          />,
        ).toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `${sanitizeFileName(filename)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 100);

        if (typeof onProgress === "function") onProgress(100);
        showToast("success", "Report downloaded.");
      } catch (_error) {
        console.error(
          "[UseExcelDownload] grouped pdf export failed:",
          _error,
        );
        showToast(
          "error",
          "Couldn't download the PDF report. Please try again.",
        );
      }
    },
    [],
  );

  const downloadLegacyAttendanceExcel = useCallback(
    async (
      groups,
      summaryHeaders,
      detailHeaders,
      filename = "report",
      _metadata = null,
      onProgress,
    ) => {
      if (!groups || groups.length === 0) {
        showToast("error", "No data to download.");
        return;
      }

      try {
        const workbook = await buildLegacyAttendanceWorkbook(
          groups,
          summaryHeaders,
          detailHeaders,
          filename,
          onProgress,
        );
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, `${sanitizeFileName(filename)}.xlsx`);
        if (typeof onProgress === "function") onProgress(100);
        showToast("success", "Report downloaded.");
      } catch (_error) {
        console.error(
          "[UseExcelDownload] legacy attendance excel export failed:",
          _error,
        );
        showToast("error", "Couldn't download the report. Please try again.");
      }
    },
    [],
  );

  const downloadLegacyAttendanceCsv = useCallback(
    async (
      groups,
      summaryHeaders,
      detailHeaders,
      filename = "report",
      _metadata = null,
      onProgress,
    ) => {
      if (!groups || groups.length === 0) {
        showToast("error", "No data to download.");
        return;
      }

      try {
        const csvContent = buildLegacyAttendanceCsvContent(
          groups,
          summaryHeaders,
          detailHeaders,
        );
        downloadCsvFile(csvContent, sanitizeFileName(filename));
        if (typeof onProgress === "function") onProgress(100);
        showToast("success", "Report downloaded.");
      } catch (_error) {
        console.error(
          "[UseExcelDownload] legacy attendance csv export failed:",
          _error,
        );
        showToast(
          "error",
          "Couldn't download the CSV report. Please try again.",
        );
      }
    },
    [],
  );

  const downloadLegacyAttendancePdf = useCallback(
    async (
      groups,
      summaryHeaders,
      detailHeaders,
      filename = "report",
      _metadata = null,
      onProgress,
    ) => {
      if (!groups || groups.length === 0) {
        showToast("error", "No data to download.");
        return;
      }

      try {
        const blob = await pdf(
          <LegacyAttendancePdfDocument
            groups={groups}
            summaryHeaders={summaryHeaders}
            detailHeaders={detailHeaders}
            filename={filename}
          />,
        ).toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${sanitizeFileName(filename)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 100);
        if (typeof onProgress === "function") onProgress(100);
        showToast("success", "Report downloaded.");
      } catch (_error) {
        console.error(
          "[UseExcelDownload] legacy attendance pdf export failed:",
          _error,
        );
        showToast(
          "error",
          "Couldn't download the PDF report. Please try again.",
        );
      }
    },
    [],
  );

  return {
    downloadCsv,
    downloadExcel,
    downloadPdf,
    downloadGroupedExcel,
    downloadGroupedCsv,
    downloadGroupedPdf,
    downloadLegacyAttendanceExcel,
    downloadLegacyAttendanceCsv,
    downloadLegacyAttendancePdf,
  };
};
